import { useEffect, useMemo, useRef, useState } from "react";

// Per-collection table column preferences: which columns show, in what order,
// and how wide. Kept in localStorage (per device) — the desktop app pins its
// origin, so these survive restarts.
//
// Stored shape: { order: [key], hidden: [key], widths: { key: px } }
// Keys are field keys plus the built-in pseudo-columns below.

export const CATEGORY_COL = "__category";
export const TAGS_COL = "__tags";

const DEFAULT_MAX = 30; // show (nearly) every field by default; hide via the Columns menu
const NOT_DEFAULT = new Set(["longtext", "richtext", "checklist"]);

const storageKey = (etId) => `ubos.columns.${etId}`;

function load(etId) {
  try {
    const raw = window.localStorage.getItem(storageKey(etId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function save(etId, prefs) {
  try {
    window.localStorage.setItem(storageKey(etId), JSON.stringify(prefs));
  } catch {
    /* storage unavailable — prefs just won't persist */
  }
}

function defaultPrefs(fields) {
  const shown = fields.filter((f) => !NOT_DEFAULT.has(f.type)).slice(0, DEFAULT_MAX);
  const shownKeys = new Set(shown.map((f) => f.key));
  // A catalogue's photo matters more than its 11th text field — always show it.
  const photo = fields.find((f) => f.type === "image");
  if (photo) shownKeys.add(photo.key);
  return {
    order: [...fields.map((f) => f.key), CATEGORY_COL, TAGS_COL],
    hidden: fields.filter((f) => !shownKeys.has(f.key)).map((f) => f.key),
    widths: {},
  };
}

/**
 * Returns every column (field or pseudo-column) in display order with a
 * `visible` flag, plus setters. Fields added after prefs were saved are
 * appended (visible) so new fields never silently disappear.
 */
export function useColumnPrefs(etId, fields, { showCategory = true } = {}) {
  const [stored, setStored] = useState(() => (etId ? load(etId) : null));

  useEffect(() => { setStored(etId ? load(etId) : null); }, [etId]);

  const prefs = useMemo(() => stored || defaultPrefs(fields), [stored, fields]);

  const all = useMemo(() => {
    const pseudo = [
      ...(showCategory ? [{ key: CATEGORY_COL, label: "Category", pseudo: true }] : []),
      { key: TAGS_COL, label: "Tags", pseudo: true },
    ];
    const byKey = Object.fromEntries([...fields, ...pseudo].map((c) => [c.key, c]));
    const seen = new Set();
    const out = [];
    for (const k of prefs.order || []) {
      if (byKey[k] && !seen.has(k)) { out.push(byKey[k]); seen.add(k); }
    }
    // Fields added since the prefs were saved go with the other fields (before
    // Category/Tags), not at the very end.
    for (const c of fields) {
      if (seen.has(c.key)) continue;
      const firstPseudo = out.findIndex((x) => x.pseudo);
      if (firstPseudo === -1) out.push(c); else out.splice(firstPseudo, 0, c);
      seen.add(c.key);
    }
    for (const c of pseudo) {
      if (!seen.has(c.key)) { out.push(c); seen.add(c.key); }
    }
    const hidden = new Set(prefs.hidden || []);
    return out.map((c) => ({ ...c, visible: !hidden.has(c.key) }));
  }, [prefs, fields, showCategory]);

  // Read/write through a ref so several changes in one tick (or before the
  // next render) build on each other instead of on a stale snapshot.
  const latest = useRef({ prefs, all });
  latest.current = { prefs, all };

  const update = (patch) => {
    const { prefs: p, all: a } = latest.current;
    const next = { ...p, order: a.map((c) => c.key), ...patch };
    latest.current = { prefs: next, all: a };
    setStored(next);
    if (etId) save(etId, next);
  };

  const toggle = (key) => {
    const hidden = new Set(latest.current.prefs.hidden || []);
    if (hidden.has(key)) hidden.delete(key); else hidden.add(key);
    update({ hidden: [...hidden] });
  };

  const move = (key, delta) => {
    const p = latest.current.prefs;
    const known = latest.current.all.map((c) => c.key);
    const order = (p.order || []).filter((k) => known.includes(k));
    known.forEach((k) => { if (!order.includes(k)) order.push(k); });
    const i = order.indexOf(key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    update({ order });
  };

  const setWidth = (key, px) => {
    const widths = { ...(latest.current.prefs.widths || {}) };
    if (px == null) delete widths[key]; else widths[key] = Math.round(px);
    update({ widths });
  };

  const showAll = () => update({ hidden: [] });

  // Make sure a column is visible (used right after "Add column"). Saving the
  // prefs also pins the current layout, so the new field isn't hidden by the
  // "first N fields" default.
  const show = (key) => {
    const hidden = (latest.current.prefs.hidden || []).filter((k) => k !== key);
    update({ hidden });
  };

  const reset = () => {
    setStored(null);
    try { window.localStorage.removeItem(storageKey(etId)); } catch { /* ignore */ }
  };

  return {
    all,
    visible: all.filter((c) => c.visible),
    widths: prefs.widths || {},
    toggle, move, setWidth, showAll, show, reset,
  };
}
